import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { CalendarToday } from '@mui/icons-material'
import { Dayjs } from 'dayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectCourses,
  selectSelectedCourse,
  selectedCourseChanged,
  selectCoursesStatus,
  fetchCourses,
} from 'src/v3/scribe/scribe.slice'
import { SearchIcon } from 'src/v3/core/icons'
import { useEffect } from 'react'

type LicenseType = 'Nurse' | 'EMT' | 'Paramedic' | 'AEMT' | 'Other'

interface ScribeFiltersProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  licenseType: LicenseType | null
  onStartDateChange: (date: Dayjs | null) => void
  onEndDateChange: (date: Dayjs | null) => void
  onLicenseTypeChange: (type: LicenseType | null) => void
  onGenerateSentimentAnalysis: () => void
  sentimentAnalysisLoading: boolean
  selectedCourse: any
}

let licenseTypeOptions: LicenseType[] = [
  'Nurse',
  'EMT',
  'Paramedic',
  'AEMT',
  'Other',
]

export default function ScribeFilters({
  startDate,
  endDate,
  licenseType,
  onStartDateChange,
  onEndDateChange,
  onLicenseTypeChange,
  onGenerateSentimentAnalysis,
  sentimentAnalysisLoading,
  selectedCourse,
}: ScribeFiltersProps) {
  let dispatch = useAppDispatch()
  let courses = useAppSelector(selectCourses)
  let selectedCourseId = useAppSelector(selectSelectedCourse)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Dashboard Filters
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box className="grid grid-cols-12 gap-4">
          {/* Course Selection */}
          <Box className="col-span-12 md:col-span-6 lg:col-span-4">
            <Autocomplete
              options={courses}
              value={
                courses.find((course) => course.id === selectedCourseId) || null
              }
              onChange={(_, value) => {
                dispatch(selectedCourseChanged(value?.id || ''))
              }}
              getOptionLabel={(option) => `${option.name} | ${option.id}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={coursesStatus === 'pending'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Course"
                  placeholder="Choose a course..."
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.slotProps.input.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Start Date */}
          <Box className="col-span-12 md:col-span-6 lg:col-span-4">
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={onStartDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,

                  slotProps: {
                    input: {
                      startAdornment: (
                        <CalendarToday
                          sx={{ mr: 1, color: 'text.secondary' }}
                        />
                      ),
                    },
                  },
                },
              }}
            />
          </Box>

          {/* End Date */}
          <Box className="col-span-12 md:col-span-6 lg:col-span-4">
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={onEndDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,

                  slotProps: {
                    input: {
                      startAdornment: (
                        <CalendarToday
                          sx={{ mr: 1, color: 'text.secondary' }}
                        />
                      ),
                    },
                  },
                },
              }}
            />
          </Box>

          {/* License Type */}
          <Box className="col-span-12">
            <Autocomplete
              options={licenseTypeOptions}
              value={licenseType}
              onChange={(_, newValue) => onLicenseTypeChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="License Type"
                  placeholder="Select a license type..."
                  helperText="Choose one license type for sentiment analysis"
                />
              )}
            />
          </Box>

          {/* Generate Sentiment Analysis Button */}
          <Box className="col-span-12">
            <Button
              color="secondary"
              variant="contained"
              onClick={onGenerateSentimentAnalysis}
              disabled={
                sentimentAnalysisLoading ||
                !selectedCourse ||
                !startDate ||
                !endDate ||
                !licenseType ||
                selectedCourse.kind === 'Live'
              }
              startIcon={
                sentimentAnalysisLoading ? <CircularProgress size={20} /> : null
              }
              sx={{
                mt: 2,
              }}
            >
              {sentimentAnalysisLoading
                ? 'Generating Analysis...'
                : 'Generate Sentiment Analysis'}
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </Paper>
  )
}
